import {useInfiniteQuery} from '@tanstack/react-query';
import ProductListingPlaceholder from "../placeholders/product/ProductListingPlaceholder";
import React, {useContext, useEffect, useState} from "react";
import ProductListing from "./ProductListing";
import Button from "../Button";
import styled from "styled-components";
import Image from "next/legacy/image";
import {useMessages} from "../../hooks/useMessages";
import {IListing} from "../../interfaces/IListing";
import {IFilter} from "../../interfaces/IFilter";
import AttributeFilter from "../filters/AttributeFilter";
import {Col, Row, Spinner} from "react-bootstrap";
import SearchFilter from "../filters/SearchFilter";
import {UrlAttrFilterQueryContext} from "../../contexts/UrlAttrFilterQueryContext";
import {ListingSortingContext} from "../../contexts/ListingSortingContext";
import {useRouter} from "next/router";
import {FilterSelectionExpectedListingCountContext} from "../../contexts/FilterSelectionExpectedListingCount";
import useHorizontalScroll from "../../hooks/useHorizontalScroll";
import {getNavPath} from "../../shared/navigateUtil";
import Link from "next/link";
import {scrollbarTrack} from "../../shared/mixins";
import {ClientContext} from "../../contexts/ClientContext";
import {IClickSource} from "../../interfaces/IClickSource";
import {useSetAtom} from "jotai";
import {categoryDescriptionAtom} from "../../store/categoryDescriptionAtom";
import {getTheme} from "../../shared/themeUtils";
import {useInView} from 'react-intersection-observer';

const Contain = styled.div`
  position: relative;
  
  .results-count {
    font-weight: bold;
    font-size: ${getTheme("fontSizes.large")};
    padding: ${getTheme("space.1")} 0;

    @media (max-width: ${getTheme("breakpoints.0")}) {
      padding: 0;
      font-size: ${getTheme("fontSizes.small")};
    }
  }
  
  .load-more {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: ${getTheme("space.2")} 0;
  }

  .filter-col {
    margin-bottom: ${getTheme("space.2")};
  }
`

const ListingLayout = styled.div`
  display: flex;
`

const Sidebar = styled.div`
  display: none;
  min-width: 200px;
  font-size: ${getTheme("fontSizes.small")};
  max-height: 5000px;
  overflow: auto;
  padding-right: ${getTheme("space.1")};
  margin-right: ${getTheme("space.2")};
  ${scrollbarTrack};
  
  .side-section-title {
    font-weight: bold;
    font-size: ${getTheme("fontSizes.medium")};
    margin-top: ${getTheme("space.2")};
    margin-bottom: ${getTheme("space.1")};
  }
  
  .side-link {
    margin-bottom: ${getTheme("space.2")};
    cursor: pointer;
  }

  @media (min-width: ${getTheme("breakpoints.2")}) {
    display: inline-block;
  }
`

const EmptyListing = styled.div`
    min-height: ${getTheme("space.6")};
    margin-bottom: ${getTheme("space.4")};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: ${getTheme("colors.secondaryFont")};
`

const MobileFiltersTrigger = styled.div`
  display: none;
  background-color: ${getTheme("colors.background")};
  border-radius: 24px;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  width: ${getTheme("space.4")};
  position: absolute;
  left: 0;
  top: 0;
  z-index: 9;
  padding: ${getTheme("space.1")} ${getTheme("space.2")};

  .filter-icon {
    margin: ${getTheme("space.0")};
  }

  @media (max-width: ${getTheme("breakpoints.1")}) {
    display: inline-flex;
  }
`;

const ScrollHide = styled.div`
  .attributes {
    padding-top: ${getTheme("space.2")};
  }
  
  @media (max-width: ${getTheme("breakpoints.1")}) {
    overflow: hidden;
    height: 60px;
    margin-bottom: ${getTheme("space.1")};
    margin-top: ${getTheme("space.2")};
    position: relative;

    .attributes {
      padding-top: 0;
    }

    .filters-mobile-wrap {
      flex-wrap: nowrap;
      overflow: auto;
    }
  }
`

type LoadMoreProductListingProps = {
  queryKeyArray: any;
  fetchFunction: any;
  expectedListingCountRequestUrl: string;
  allowShowEmptyListing?: boolean;
  clickSource?: IClickSource;
}

const PAGE_SIZE = 30;

const LoadMoreProductListing = (props: LoadMoreProductListingProps) => {
  const {scrollRef, scrollProps} = useHorizontalScroll("reverse");
  const messagesQuery = useMessages()
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const {selectedSortingOptionCtx} = useContext(ListingSortingContext)
  const {urlFilterQueryCtx, setUrlFilterQueryCtx} = useContext(UrlAttrFilterQueryContext)
  const {setFilterSelectionExpectedListingCountCtx} = useContext(FilterSelectionExpectedListingCountContext)
  const [keepPreviousData, setKeepPreviousData] = useState(true)
  const router = useRouter()
  const {clientCtx} = useContext(ClientContext)
  const setCategoryDescription = useSetAtom(categoryDescriptionAtom)
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
  });

  const infiniteQuery = useInfiniteQuery<IListing>(
    [...props.queryKeyArray, router.asPath, urlFilterQueryCtx], props.fetchFunction,
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage: any, allPages: any) => {
        let calcOffset = 0;
        for (let i = 0; i < allPages.length; i++) {
          if (allPages[i].products) {
            calcOffset += allPages[i].products.length;
          }
        }
        if (allPages[0].filterProductCount > calcOffset) {
          return calcOffset
        } else {
          return false;
        }
      },
    });


  useEffect(() => {
    if (router.query.filter) {
      // set filter context
      setUrlFilterQueryCtx(encodeURIComponent(router.query.filter.toString()))
    } else {
      setUrlFilterQueryCtx("")
    }

    // Handle any response redirects
    if (infiniteQuery.data && infiniteQuery.data.pages[0].redirectTo != null) {
      router.push(`/${infiniteQuery.data.pages[0].redirectTo}`)
    }

    if (infiniteQuery.data && infiniteQuery.data.pages[0].filterProductCount) {
      setFilterSelectionExpectedListingCountCtx(infiniteQuery.data.pages[0].filterProductCount)
    }

    if (infiniteQuery.data && infiniteQuery.data.pages[0].contentText) {
      setCategoryDescription(infiniteQuery.data.pages[0].contentText)
    }

    // Todo - trigger on slug change if possible, ignoring query params
    router.events.on("routeChangeStart", () => {
      setKeepPreviousData(false)
    })

    router.events.on("routeChangeComplete", () => {
      setKeepPreviousData(true)
    })

    return () => {
      setCategoryDescription("")
    }
  }, [router, infiniteQuery.data, selectedSortingOptionCtx])

  useEffect(() => {
    if (inView && infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
      infiniteQuery.fetchNextPage();
    }
  }, [inView, infiniteQuery.hasNextPage, infiniteQuery.isFetchingNextPage]);

  const loadMoreRemaining = () => {
    if (infiniteQuery.data) {
      const totalProds = infiniteQuery.data?.pages[0].filterProductCount;
      let calcOffset = 0;
      if (infiniteQuery.data) {
        for (let i = 0; i < infiniteQuery.data.pages.length; i++) {
          calcOffset += infiniteQuery.data.pages[i].products.length;
        }
      }

      return totalProds - calcOffset;
    }
  }


  const hasCategories = (query: any) => {
    return query.data.pages[0].categories && (query.data.pages[0].categories.length > 0);
  }


  const hasBrands = (query: any) => {
    return query.data.pages[0].brands && (query.data.pages[0].brands.length > 0);
  }

  const hasSeries = (query: any) => {
    return query.data.pages[0].series && (query.data.pages[0].series.length > 0);
  }

  const hasSubSeries = (query: any) => {
    return query.data.pages[0].subSeries && (query.data.pages[0].subSeries.length > 0);
  }

  const hasMagazineArticles = (query: any) => {
    return query.data.pages[0].magazines && (query.data.pages[0].magazines.length > 0);
  }

  return (
    <Contain>
      <>
        {/*Loading Placeholder*/}
        {(!infiniteQuery.isSuccess || infiniteQuery.data.pages[0].redirectTo) &&
          <div>
            <ProductListingPlaceholder key="1"/>
            <ProductListingPlaceholder key="2"/>
            <ProductListingPlaceholder key="3"/>
            <ProductListingPlaceholder key="4"/>
            <ProductListingPlaceholder key="5"/>
          </div>
        }



        {/* Results count */}
        {messagesQuery.data && infiniteQuery.data && !infiniteQuery.data.pages[0].redirectTo && infiniteQuery.data.pages[0].totalProductCount > 0 && infiniteQuery.data.pages[0].filterProductCount &&
          <div className="results-count">
            {infiniteQuery.data.pages[0].filterProductCount != infiniteQuery.data.pages[0].totalProductCount &&
              <>
                {infiniteQuery.data.pages[0].filterProductCount} {messagesQuery.data.messages["of"]} {infiniteQuery.data.pages[0].totalProductCount} {messagesQuery.data.messages["products"]}
              </>
            }

            {infiniteQuery.data.pages[0].filterProductCount == infiniteQuery.data.pages[0].totalProductCount &&
              <>
                {infiniteQuery.data.pages[0].totalProductCount} {messagesQuery.data.messages["products"]}
              </>
            }
          </div>
        }

        {/*Filters */}
        {(infiniteQuery.data?.pages[0].filters && infiniteQuery.data?.pages[0].filters.length > 0) &&
          <ScrollHide>
            <Row className="filters-mobile-wrap attributes" ref={scrollRef} {...scrollProps}>

              {(infiniteQuery.isSuccess && messagesQuery.data && (infiniteQuery.data?.pages[0].filters)) &&
                <Col xs="5" sm="5" md="4" xl="3" className="filter-col">
                  <SearchFilter name={messagesQuery.data.messages["search-button"]}/>
                </Col>
              }

              {(infiniteQuery.data?.pages[0].filters) && infiniteQuery.data?.pages[0].filters.map((filter: IFilter) => {
                return (
                  <Col key={filter.id} xs="6" sm="6" md="4" xl="3" className="filter-col">
                    <AttributeFilter
                      name={filter.text}
                      tip={filter.tip}
                      type={filter.filterType}
                      rangeValueLabel={filter.filterType == "range" ? clientCtx?.currency : ""}
                      options={filter.values}
                      filter={filter}
                      expectedListingCountRequestUrl={props.expectedListingCountRequestUrl}
                    />
                  </Col>
                )
              })}
            </Row>
          </ScrollHide>
        }

        <ListingLayout>
          {(infiniteQuery.isSuccess && (hasCategories(infiniteQuery) || hasBrands(infiniteQuery))) &&
            <Sidebar>
              <div className="sidebar-content">
                {/* Categories listing - in search */}
                {(infiniteQuery.isSuccess && messagesQuery.data && hasCategories(infiniteQuery)) &&
                  <div className="side-section-title">{messagesQuery.data.messages["categories"]}</div>}
                {(infiniteQuery.isSuccess && hasCategories(infiniteQuery)) && infiniteQuery.data?.pages[0].categories?.map(category => {
                  return (
                    <div key={category.id} className="side-link">
                      <Link href={getNavPath(router, `cat/${category.slug}`)}>
                        {category.name}
                      </Link>
                    </div>
                  )
                })}

                {/* Brands listing - in search */}
                {(infiniteQuery.isSuccess && messagesQuery.data && hasBrands(infiniteQuery)) &&
                  <div className="side-section-title">{messagesQuery.data.messages["brands"]}</div>}
                {(infiniteQuery.isSuccess && infiniteQuery.data?.pages[0].brands) && infiniteQuery.data?.pages[0].brands.map(brand => {
                  return (
                    <div key={brand.brandID} className="side-link">
                      <Link href={getNavPath(router, `brand/${brand.slug}`)}>
                        {brand.brandName}
                      </Link>
                    </div>
                  )
                })}

                {/* Series listing - in search */}
                {(infiniteQuery.isSuccess && messagesQuery.data && hasSeries(infiniteQuery)) &&
                  <div className="side-section-title">{messagesQuery.data.messages["series"]}</div>}
                {(infiniteQuery.isSuccess && infiniteQuery.data?.pages[0].series) && infiniteQuery.data?.pages[0].series.map(serie => {
                  return (
                    <div key={serie.seriesID} className="side-link">
                      <Link href={getNavPath(router, `series/${serie.slug}`)}>
                        {serie.seriesName}
                      </Link>
                    </div>
                  )
                })}

                {/* SubSeries listing - in search */}
                {(infiniteQuery.isSuccess && messagesQuery.data && hasSubSeries(infiniteQuery)) &&
                  <div className="side-section-title">{messagesQuery.data.messages["subseries"]}</div>}
                {(infiniteQuery.isSuccess && infiniteQuery.data?.pages[0].subSeries) && infiniteQuery.data?.pages[0].subSeries.map(subSerie => {
                  return (
                    <div key={subSerie.subSeriesID} className="side-link">
                      <Link href={getNavPath(router, `subseries/${subSerie.slug}`)}>
                        {subSerie.subSeriesName}
                      </Link>
                    </div>
                  )
                })}

                {/* Magazine listing - in search */}
                {(infiniteQuery.isSuccess && messagesQuery.data && hasMagazineArticles(infiniteQuery)) &&
                  <div className="side-section-title">{messagesQuery.data.messages["magazine-articles"]}</div>}
                {(infiniteQuery.isSuccess && infiniteQuery.data?.pages[0].magazines) && infiniteQuery.data?.pages[0].magazines.map(article => {
                  return (
                    <div key={article.articleID} className="side-link">
                      <Link href={getNavPath(router, `magazine/${article.slug}`)}>
                        {article.title}
                      </Link>
                    </div>
                  )
                })}

              </div>
            </Sidebar>}

          {/* Products listing */}
          {(infiniteQuery.data && (infiniteQuery.data.pages[0].products && infiniteQuery.data.pages[0].products.length > 0)) &&
            <ProductListing pages={infiniteQuery.data?.pages} clickSource={props.clickSource}/>}
        </ListingLayout>

        {/* Load More */}
        {(messagesQuery.data && infiniteQuery.isSuccess && (infiniteQuery.data?.pages[0]?.products?.length > PAGE_SIZE - 2)) &&
          <div className="load-more" ref={loadMoreRef}>
            <Button variant={"primary"} onClick={() => infiniteQuery.fetchNextPage()}
                    disabled={!infiniteQuery.hasNextPage || infiniteQuery.isFetchingNextPage}>
              {infiniteQuery.isFetchingNextPage ? <Spinner animation="border" size="sm"/> :
                infiniteQuery.hasNextPage ? messagesQuery.data.messages["load-more"] + " (" + loadMoreRemaining() + ")" : messagesQuery.data.messages["you-reached-the-end"]}
            </Button>
          </div>
        }

        {props.allowShowEmptyListing != false && messagesQuery.data && infiniteQuery.data && infiniteQuery.data.pages[0]
          && infiniteQuery.data.pages[0].products && infiniteQuery.data.pages[0].products.length == 0 &&
          <EmptyListing>
            <Image src="/icons/live-music.svg" width={150} height={200} alt="Empty Cart"/>
            {messagesQuery.data && <span>{messagesQuery.data.messages["no-products-to-show"]}</span>}
          </EmptyListing>
        }

        {/* If it's search page and we get 500 it means search webservice is down */}
        {/* @ts-ignore */}
        {infiniteQuery.data && (infiniteQuery.data.pages[0].errorID || infiniteQuery.data.errorID) && router.asPath.includes("search") &&
          <EmptyListing>
            <Image src="/icons/live-music.svg" width={150} height={200} alt="Empty Cart"/>
            {messagesQuery.data && <span>{messagesQuery.data.messages["error-search-service-unavailable"]}</span>}
          </EmptyListing>
        }
      </>
    </Contain>
  )
}

export default LoadMoreProductListing;
